<template>
  <div class="page">
    <div class="head">
      <div>
        <h2 class="title">Messages</h2>
        <div class="sub">Internal notes, tasks, follow-ups. ✅</div>
      </div>

      <div class="actions">
        <router-link
          v-if="role === 'super_admin' || role === 'branch_admin' || role === 'branch_manager'"
          class="btn"
          :to="{ name: 'messages-compose' }"
        >
          + New Message
        </router-link>


        <button class="btn secondary" @click="refresh" :disabled="loading">
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div v-if="!hasBackend" class="warn">
      Messaging needs backend login (JWT token). Right now your auth store is demo-mode.
      Once token auth is wired, this inbox becomes live.
    </div>

    <div v-if="error" class="err">{{ error }}</div>

    <div class="grid">
      <div class="card">
        <div class="card__head">
          <div class="card__title">Inbox</div>
          <div class="chip">{{ unreadCount }} unread</div>
        </div>

        <div v-if="!safeInbox.length" class="empty">No messages.</div>

        <div v-else class="list">
          <button
            v-for="m in safeInbox"
            :key="m._id"
            class="row"
            @click="open(m)"
          >
            <div class="left">
              <div class="top">
                <span class="from">{{ m._fromName || m._fromUserId }}</span>
                <span class="dot" v-if="String(m._status).toUpperCase()==='UNREAD'"></span>
                <span class="prio" :class="'prio--' + String(m._priority).toLowerCase()">{{ m._priority }}</span>
              </div>

              <div class="subj">{{ m._subject || '(no subject)' }}</div>
              <div class="meta">
                <span v-if="m._scopeType && m._scopeType !== 'NONE'">
                  {{ m._scopeType }}#{{ m._scopeId }}
                </span>
                <span class="muted">{{ fmt(m._createdAt) }}</span>
              </div>
            </div>

            <div class="right">
              <span class="status" :class="'st--' + String(m._status).toLowerCase()">{{ m._status }}</span>
            </div>
          </button>
        </div>
      </div>

      <div class="card">
        <div class="card__head">
          <div class="card__title">Sent</div>
        </div>

        <div v-if="!safeSent.length" class="empty">No sent messages.</div>

        <div v-else class="list">
          <div v-for="m in safeSent" :key="m._id" class="row row--static">
            <div class="left">
              <div class="top">
                <span class="to">To: {{ m._toName || m._toUserId }}</span>
                <span class="prio" :class="'prio--' + String(m._priority).toLowerCase()">{{ m._priority }}</span>
              </div>
              <div class="subj">{{ m._subject || '(no subject)' }}</div>
              <div class="meta">
                <span v-if="m._scopeType && m._scopeType !== 'NONE'">
                  {{ m._scopeType }}#{{ m._scopeId }}
                </span>
                <span class="muted">{{ fmt(m._createdAt) }}</span>
              </div>
            </div>
            <div class="right">
              <span class="status st--sent">SENT</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="active" class="modal" @click.self="active=null">
      <div class="modal__box">
        <div class="modal__head">
          <div class="modal__title">{{ active._subject || '(no subject)' }}</div>
          <button class="x" @click="active=null">✕</button>
        </div>

        <div class="modal__meta">
          <div><b>From:</b> {{ active._fromName || active._fromUserId }}</div>
          <div><b>Priority:</b> {{ active._priority }}</div>
          <div><b>Status:</b> {{ active._status }}</div>
          <div v-if="active._scopeType && active._scopeType !== 'NONE'">
            <b>Scope:</b> {{ active._scopeType }} #{{ active._scopeId }}
          </div>
          <div class="muted">{{ fmt(active._createdAt) }}</div>
        </div>

        <div class="modal__body">{{ active._body }}</div>

        <div class="modal__actions">
          <!-- Only show Mark Read when it is UNREAD -->
          <button
            v-if="String(active._status).toUpperCase() === 'UNREAD'"
            class="btn secondary"
            @click="markRead(active)"
            :disabled="!hasBackend"
          >Mark Read</button>

          <button
            class="btn secondary"
            @click="reply(active)"
            :disabled="!hasBackend"
            title="Reply to this message"
          >Reply</button>

          <button class="btn" @click="markDone(active)" :disabled="!hasBackend">Mark Done</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'MessagesInbox',
  data () {
    return { active: null }
  },
  computed: {
    ...mapGetters('messages', ['inbox', 'sent', 'unreadCount', 'isLoading', 'error']),
    loading () { return this.isLoading },

    // ✅ correct token key
    hasBackend () {
      try { return !!localStorage.getItem('edda_token') } catch (e) { return false }
    },
    currentUser () {
      return this.$store.getters['auth/currentUser'] || {}
    },
    role () {
      return (this.currentUser && this.currentUser.role) ? this.currentUser.role : ''
    },
    isEmployee () {
      return this.role === 'employee'
    },

    // Defensive arrays so ".length" never explodes
    safeInbox () {
      const rows = Array.isArray(this.inbox) ? this.inbox : []
      return rows.map(this.normalizeMsg)
    },
    safeSent () {
      const rows = Array.isArray(this.sent) ? this.sent : []
      return rows.map(this.normalizeMsg)
    }
  },
  created () {
    if (this.hasBackend) this.refresh()
  },

  methods: {
    normalizeMsg (m) {
      // support both camelCase and snake_case
      const x = m || {}
      return {
        ...x,
        _id: x.id,
        _fromUserId: x.fromUserId || x.from_user_id,
        _toUserId: x.toUserId || x.to_user_id,
        _fromName: x.fromName || x.from_name,
        _toName: x.toName || x.to_name,
        _scopeType: x.scopeType || x.scope_type || 'NONE',
        _scopeId: x.scopeId || x.scope_id || null,
        _priority: x.priority || 'NORMAL',
        _status: x.status || 'UNREAD',
        _subject: x.subject || '',
        _body: x.body || '',
        _createdAt: x.createdAt || x.created_at || x.created || null
      }
    },

    async refresh () {
      await this.$store.dispatch('messages/refresh')
    },

    fmt (x) {
      if (!x) return ''
      try { return new Date(x).toLocaleString() } catch (e) { return String(x) }
    },

    // ✅ AUTO-READ ON OPEN (and refresh list immediately)
    async open (m) {
      this.active = m

      // if no backend token, just show the modal and stop
      if (!this.hasBackend) return

      if (String(m._status).toUpperCase() === 'UNREAD') {
        try {
          await this.$store.dispatch('messages/markRead', m._id)
          this.active = { ...this.active, _status: 'READ' }
          await this.refresh()
        } catch (e) {
          console.warn('markRead failed', e)
        }
      }
    },


    async markRead (m) {
      if (!this.hasBackend) return
      await this.$store.dispatch('messages/markRead', m._id)
      this.active = { ...this.active, _status: 'READ' }
      await this.refresh()
    },

    async markDone (m) {
      if (!this.hasBackend) return
      await this.$store.dispatch('messages/markDone', m._id)
      this.active = null
      await this.refresh()
    },


    // ✅ REPLY WORKS NO MATTER HOW DEEP THE CHAIN IS
    reply (m) {
      if (!m) return

      const subj = (m._subject || '').trim()
      const subject = subj
        ? (subj.toLowerCase().startsWith('re:') ? subj : ('Re: ' + subj))
        : 'Re:'

      const quotedMeta = [
        `On ${this.fmt(m._createdAt)}, ${m._fromName || m._fromUserId} wrote:`,
        (m._body || '').split('\n').map(l => '> ' + l).join('\n')
      ].join('\n')

      this.$router.push({
        name: 'messages-compose',
        query: {
          replyTo: String(m._id),
          subject,
          body: `\n\n${quotedMeta}`
        }
      })
    }

  }
}
</script>

<style scoped>
/* keep your existing styles exactly (unchanged) */
.page { max-width: 1200px; margin: 0 auto; }
.head { display:flex; justify-content:space-between; gap:12px; align-items:flex-start; margin-bottom: 14px; }
.title { margin:0; }
.sub { color:#6b7280; margin-top:4px; }
.actions { display:flex; gap:8px; align-items:center; }

.grid { display:grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 900px){ .grid{ grid-template-columns: 1fr; } }

.card { background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding: 12px; }
.card__head { display:flex; justify-content:space-between; align-items:center; margin-bottom: 10px; }
.card__title { font-weight: 800; }
.chip { font-size:12px; font-weight:800; padding:6px 10px; border-radius:999px; border:1px solid #e5e7eb; background:#f9fafb; }

.list { display:flex; flex-direction:column; gap:8px; }
.row {
  width:100%;
  display:flex;
  justify-content:space-between;
  gap:10px;
  text-align:left;
  background:#fff;
  border:1px solid #eef2f7;
  border-radius:10px;
  padding:10px;
  cursor:pointer;
}
.row:hover { box-shadow: 0 1px 0 rgba(0,0,0,0.03); }
.row--static { cursor: default; }
.left { min-width: 0; }
.top { display:flex; gap:10px; align-items:center; }
.from, .to { font-weight: 800; }
.dot { width:8px; height:8px; border-radius:99px; background:#ef4444; display:inline-block; }
.subj { font-weight:700; margin-top:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.meta { display:flex; gap:10px; margin-top:6px; font-size:12px; color:#6b7280; }

.status { font-size:12px; font-weight:900; padding:6px 10px; border-radius:999px; border:1px solid #e5e7eb; }
.st--unread { border-color:#fecaca; color:#b91c1c; background:#fff5f5; }
.st--read { border-color:#e5e7eb; color:#111827; background:#f9fafb; }
.st--done { border-color:#bbf7d0; color:#166534; background:#f0fdf4; }
.st--sent { border-color:#e5e7eb; color:#111827; background:#f9fafb; }

.prio { font-size:11px; font-weight:900; padding:4px 8px; border-radius:999px; border:1px solid #e5e7eb; }
.prio--low { opacity:0.7; }
.prio--high { border-color:#f59e0b; background:#fffbeb; color:#92400e; }

.warn { padding:10px 12px; border-radius:10px; border:1px solid #fde68a; background:#fffbeb; color:#92400e; margin-bottom: 10px; }
.err { padding:10px 12px; border-radius:10px; border:1px solid #fecaca; background:#fff5f5; color:#b91c1c; margin-bottom: 10px; }
.empty { color:#6b7280; padding: 12px; }

.modal { position:fixed; inset:0; background: rgba(0,0,0,0.4); display:flex; align-items:center; justify-content:center; z-index: 999; }
.modal__box { width:min(720px, 92vw); background:#fff; border-radius:14px; border:1px solid #e5e7eb; overflow:hidden; }
.modal__head { display:flex; justify-content:space-between; gap:10px; padding:12px 14px; border-bottom:1px solid #eef2f7; }
.modal__title { font-weight: 900; }
.x { border:none; background:transparent; cursor:pointer; font-size:16px; }
.modal__meta { padding: 10px 14px; color:#111827; display:grid; gap:4px; }
.modal__body { padding: 12px 14px; white-space: pre-wrap; border-top:1px dashed #eef2f7; border-bottom:1px dashed #eef2f7; }
.modal__actions { display:flex; gap:10px; justify-content:flex-end; padding: 12px 14px; }
.muted { color:#6b7280; }
.btn { background:#fff; border:1px solid #fde2e2; color:#e00000; border-radius:10px; padding:9px 14px; font-weight:800; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; }
.btn.secondary { border-color:#e5e7eb; color:#111827; }
.btn:disabled { opacity:0.6; cursor:not-allowed; }
</style>
